import * as React from 'react';
import ConfigurationService from "../../../Service/ConfigurationService";
import { useEffect, useRef, useState } from "react";
import { Empty } from 'antd';

interface CarrouselItemProps {
  imageUrl: string;
  onClick?: (url: string) => void;
  onLoad?: Function;
  isActive?: boolean;
}

// A gesture whose pointer moved further than this is the carousel being swiped, not a tap on the
// photo. The carousel follows the pointer, so a swipe moves far more than a few pixels.
const TAP_MAX_MOVEMENT = 10;

interface Tap {
  pointerId: number;
  onUp: (event: PointerEvent) => void;
  onCancel: (event: PointerEvent) => void;
}

// Stops following the gesture being tracked, if any, and drops its window listeners.
function releaseTap(tapRef: React.MutableRefObject<Tap | null>) {
  const tap = tapRef.current;
  if (!tap) return;
  tapRef.current = null;
  window.removeEventListener('pointerup', tap.onUp, true);
  window.removeEventListener('pointercancel', tap.onCancel, true);
}

export default function CarouselItem(props: CarrouselItemProps) {
  const { imageUrl, onClick } = props;

  // The gesture being followed, see `handlePhotoPointerDown`. It is kept in a ref because the
  // listeners it installs live on the window, outside the React tree.
  const tapRef = useRef<Tap | null>(null);

  // Handle cases when photo can't be loaded
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  useEffect(() => () => releaseTap(tapRef), []);

  const getImageUrl = (imageUrl: string) => {
    // `data:`, `http` and `blob:` URLs are already complete. The `blob:` one is the photo that was
    // just taken (the popup previews it before it is queued): prefixing it with the API base URL
    // turned it into a dead link, so the preview stayed broken and the full screen viewer was asked
    // to open a URL that cannot load.
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('http') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }
    const baseUrl = ConfigurationService.urls().baseApiUrl;
    const separator = imageUrl.startsWith('/') ? '' : '/';
    return `${baseUrl}${separator}${imageUrl}`;
  }

  const fullUrl = getImageUrl(imageUrl);

  if (!imageUrl || failed) {
    return (
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <Empty description={<span style={{ color: 'black' }}>No image found</span>} />
      </div>
    );
  }

  // The trees list copy keeps the full photo and nothing else (`Save Offline`), so that is what the popup shows, the browser
  // scaling it down to the window: the same file opens full screen on a tap
  const activeUrl = fullUrl;

  // Ensure swype + direct click on the photo opens the PhotoViewer without waiting
  const handlePhotoPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    releaseTap(tapRef);
    // Only the primary button of the primary pointer is a tap: a second finger is a pinch, and a
    // right/middle click used to do nothing on the photo.
    if (!e.isPrimary || e.button !== 0) return;

    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startY = e.clientY;

    const onUp = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      releaseTap(tapRef);
      const movedX = Math.abs(event.clientX - startX);
      const movedY = Math.abs(event.clientY - startY);
      if (movedX > TAP_MAX_MOVEMENT || movedY > TAP_MAX_MOVEMENT) return;
      onClick?.(activeUrl);
    };

    const onCancel = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      releaseTap(tapRef);
    };

    tapRef.current = { pointerId, onUp, onCancel };
    // Captured, so the release is seen even when it happens outside the photo, and never swallowed
    // by the carousel, which stops the propagation of the events it handles itself.
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onCancel, true);
  };

  return (
    <div className="map-tree description">
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src={activeUrl}
          alt="tree image"
          style={{
            cursor: 'pointer',
            objectFit: 'contain',
          }}
          onPointerDown={handlePhotoPointerDown}
          // `click` is kept as a second path on purpose: a mobile browser that hands the gesture to
          // its own scrolling cancels the pointer events, so the tap above never completes, but it
          // still sends the click of a plain tap. Both open the same photo, a duplicate is harmless.
          onClick={() => onClick?.(activeUrl)}
          onError={async (event) => {
            // One line for the log: which URL failed, and whether the trees list copy holds it at all
            const image = event.target as HTMLImageElement;
            const stored = typeof caches !== 'undefined'
              ? await caches.match(image.src).catch(() => null)
              : null;
            console.warn('the tree photo could not be loaded', { src: image.src, cached: !!stored });

            setFailed(true);
          }}
        />
      </div>
    </div>
  );
}